import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

/**
 * API base URL - uses Vite proxy in development
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Token storage key
 */
const TOKEN_KEY = 'sg_auth_token';

/**
 * Create axios client with defaults
 */
const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include httpOnly cookies
});

/**
 * Request interceptor - attach JWT token from localStorage as backup
 */
client.interceptors.request.use(
  (config) => {
    // Primary auth is via httpOnly cookie, but also support Authorization header
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response interceptor - handle auth errors
 */
client.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ error?: string; message?: string }>) => {
    // Only redirect on actual auth failures, not business logic 403s
    if (error.response?.status === 401) {
      // 401 is always an auth error
      localStorage.removeItem(TOKEN_KEY);

      const isLoginPage = window.location.pathname.includes('/login');
      const isAuthEndpoint = error.config?.url?.includes('/auth/');

      if (!isLoginPage && !isAuthEndpoint) {
        window.location.href = '/admin/login';
      }
    } else if (error.response?.status === 403) {
      // 403 could be auth or business logic - check the error message
      const errorMsg = error.response.data?.error || '';
      const isAuthError = errorMsg === 'Access denied' ||
                          errorMsg === 'Unauthorized' ||
                          errorMsg === 'Authentication required';

      if (isAuthError) {
        localStorage.removeItem(TOKEN_KEY);

        const isLoginPage = window.location.pathname.includes('/login');
        const isAuthEndpoint = error.config?.url?.includes('/auth/');

        if (!isLoginPage && !isAuthEndpoint) {
          window.location.href = '/admin/login';
        }
      }
      // For business logic 403s (like "Session is locked"), just reject without redirect
    }
    return Promise.reject(error);
  },
);

export default client;

// ============================================================================
// Type Definitions
// ============================================================================

export type Role = 'admin' | 'power_user' | 'user';

export type Permission =
  | 'sessions.view_all'
  | 'sessions.delete_any'
  | 'users.view'
  | 'users.create'
  | 'users.edit'
  | 'users.delete'
  | 'settings.view'
  | 'settings.edit'
  | 'storage.view'
  | 'storage.audit'
  | 'storage.reset';

export interface User {
  id: number;
  username: string;
  role: Role;
  email: string | null;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export interface Session {
  id: string;
  admin_id: number;
  customer_name: string;
  customer_email: string;
  session_notes: string | null;
  status: 'pending' | 'in-progress' | 'approved' | 'rejected' | 'mixed';
  send_email_invite: boolean;
  notify_admin: boolean; // Whether admin receives completion notification
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  proof_link?: string; // Customer-facing proof review URL
  // Expiration fields
  expires_at: string | null;
  expiration_enabled: boolean;
  expiration_days: number;
  // Revision limit field
  max_revisions: number;
}

/**
 * Revision status for a photo
 */
export interface RevisionStatus {
  photo_id: string;
  current_revisions: number;      // How many revisions used (version_number - 1)
  max_revisions: number;          // Effective max (session + overrides)
  can_request_changes: boolean;   // current < max
  is_last_revision: boolean;      // current === max - 1
}

/**
 * Revision override created by admin
 */
export interface RevisionOverride {
  id: number;
  photo_id: string;
  session_id: string;
  admin_id: number;
  additional_revisions: number;
  reason: string;
  created_at: string;
}

export interface SessionDetails extends Session {
  photos: Photo[];
  feedback: Record<string, Feedback>;
  photo_count: number;
  feedback_count: number;
  is_locked: boolean;
  is_expired?: boolean;
}

export interface Photo {
  id: string;
  session_id: string;
  original_filename: string;
  stored_filename?: string;
  file_path?: string;
  file_size_bytes: number;
  mime_type: string | null;
  width_px?: number | null;
  height_px?: number | null;
  // Backend upload response uses these field names:
  width?: number | null;
  height?: number | null;
  url?: string;
  thumbnail_url?: string;
  display_order: number;
  created_at: string;
  // Optional display name for admin rename feature
  display_name?: string;
  // Versioning fields
  parent_photo_id?: string | null;
  version_number?: number;
  is_latest_version?: boolean;
}

export interface Feedback {
  id: string;
  session_id: string;
  photo_id: string;
  feedback_text: string;
  approval_status: 'approve' | 'needs_changes' | 'rejected';
  submitted_at: string;
  customer_ip_hash: string | null;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface SessionListResponse {
  sessions: Session[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateSessionInput {
  customer_name: string;
  customer_email: string;
  session_notes?: string;
  send_email_invite?: boolean;
  notify_admin?: boolean;
  expiration_enabled?: boolean;
  expiration_days?: number;
  max_revisions?: number;
}

export interface UpdateSessionInput {
  customer_name?: string;
  customer_email?: string;
  session_notes?: string | null;
  send_email_invite?: boolean;
  notify_admin?: boolean;
}

// ============================================================================
// Auth API
// ============================================================================

export const authAPI = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await client.post<LoginResponse>('/auth/login', {
      username,
      password,
    });
    // Store token in localStorage as backup (primary is httpOnly cookie)
    localStorage.setItem(TOKEN_KEY, response.data.token);
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await client.post('/auth/logout');
    } finally {
      localStorage.removeItem(TOKEN_KEY);
    }
  },

  async refresh(): Promise<{ token: string }> {
    const response = await client.post<{ token: string }>('/auth/refresh');
    localStorage.setItem(TOKEN_KEY, response.data.token);
    return response.data;
  },

  async getMe(): Promise<{ user: User }> {
    const response = await client.get<{ user: User }>('/auth/me');
    return response.data;
  },
};

// ============================================================================
// Sessions API
// ============================================================================

export const sessionsAPI = {
  async create(input: CreateSessionInput): Promise<{ session: Session; proof_link: string }> {
    const response = await client.post<{ session: Session; proof_link: string }>(
      '/sessions',
      input,
    );
    return response.data;
  },

  async list(page = 1, limit = 10): Promise<SessionListResponse> {
    const response = await client.get<SessionListResponse>('/sessions', {
      params: { page, limit },
    });
    return response.data;
  },

  async get(id: string): Promise<{
    session: SessionDetails;
    proof_link: string;
    revision_statuses: Record<string, RevisionStatus>;
    revision_overrides?: RevisionOverride[];
  }> {
    const response = await client.get<{
      session: SessionDetails;
      proof_link: string;
      revision_statuses: Record<string, RevisionStatus>;
      revision_overrides?: RevisionOverride[];
    }>(`/sessions/${id}`);
    return response.data;
  },

  async update(id: string, input: UpdateSessionInput): Promise<{ session: Session }> {
    const response = await client.put<{ session: Session }>(`/sessions/${id}`, input);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await client.delete(`/sessions/${id}`);
  },

  async invite(id: string): Promise<{ proof_link: string; customer_email: string }> {
    const response = await client.post<{ proof_link: string; customer_email: string }>(
      `/sessions/${id}/invite`,
    );
    return response.data;
  },

  async start(id: string): Promise<{ started_at: string }> {
    const response = await client.post<{ started_at: string }>(`/sessions/${id}/start`);
    return response.data;
  },

  async notifyRevision(id: string): Promise<{
    message: string;
    proof_link: string;
    customer_email: string;
    revised_photo_count: number;
    message_id?: string;
  }> {
    const response = await client.post<{
      message: string;
      proof_link: string;
      customer_email: string;
      revised_photo_count: number;
      message_id?: string;
    }>(`/sessions/${id}/notify-revision`);
    return response.data;
  },

  /**
   * Create a revision override for a photo (admin only)
   */
  async createRevisionOverride(
    sessionId: string,
    photoId: string,
    additionalRevisions: number,
    reason: string,
  ): Promise<{ override: RevisionOverride; revision_status: RevisionStatus }> {
    const response = await client.post<{
      override: RevisionOverride;
      revision_status: RevisionStatus;
    }>(`/sessions/${sessionId}/photos/${photoId}/override-revisions`, {
      additional_revisions: additionalRevisions,
      reason,
    });
    return response.data;
  },
};

// ============================================================================
// Photos API
// ============================================================================

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface PhotoUploadResult {
  photos: Photo[];
  failed: Array<{ filename: string; error: string }>;
}

// Backend response format (different from PhotoUploadResult)
interface BackendUploadResponse {
  success: boolean;
  message: string;
  uploaded: number;
  failed: number;
  results: Array<{
    success: boolean;
    filename: string;
    photo?: Photo;
    error?: string;
  }>;
}

export const photosAPI = {
  /**
   * Upload photos to a session
   */
  async upload(
    sessionId: string,
    files: File[],
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<PhotoUploadResult> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('photos', file);
    });

    const response = await client.post<BackendUploadResponse>(
      `/photos/${sessionId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            });
          }
        },
      },
    );

    // Transform backend response to frontend format
    const data = response.data;
    return {
      photos: data.results
        .filter((r) => r.success && r.photo)
        .map((r) => r.photo as Photo),
      failed: data.results
        .filter((r) => !r.success)
        .map((r) => ({ filename: r.filename, error: r.error || 'Unknown error' })),
    };
  },

  /**
   * List photos for a session
   */
  async list(sessionId: string): Promise<{ photos: Photo[] }> {
    const response = await client.get<{ photos: Photo[] }>(`/photos/${sessionId}`);
    return response.data;
  },

  /**
   * Reorder photos in a session
   */
  async reorder(sessionId: string, photoIds: string[]): Promise<{ photos: Photo[] }> {
    const response = await client.put<{ photos: Photo[] }>(
      `/photos/${sessionId}/reorder`,
      { photo_ids: photoIds },
    );
    return response.data;
  },

  /**
   * Delete a photo
   */
  async delete(sessionId: string, photoId: string): Promise<void> {
    await client.delete(`/photos/${sessionId}/${photoId}`);
  },

  /**
   * Upload a new version of a photo
   * Used when a customer has requested changes
   */
  async uploadVersion(
    sessionId: string,
    photoId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<{ photo: Photo; previous_version_id: string }> {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await client.post<{
      success: boolean;
      message: string;
      photo: Photo;
      previous_version_id: string;
    }>(
      `/photos/${sessionId}/${photoId}/version`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            });
          }
        },
      },
    );

    return {
      photo: response.data.photo,
      previous_version_id: response.data.previous_version_id,
    };
  },

  /**
   * Get version history for a photo
   */
  async getVersionHistory(sessionId: string, photoId: string): Promise<{ versions: Photo[] }> {
    const response = await client.get<{ success: boolean; versions: Photo[]; count: number }>(
      `/photos/${sessionId}/${photoId}/versions`,
    );
    return { versions: response.data.versions };
  },

  /**
   * Rename a photo (update display_name)
   */
  async rename(sessionId: string, photoId: string, displayName: string | null): Promise<Photo> {
    const response = await client.patch<{ success: boolean; photo: Photo }>(
      `/photos/${sessionId}/${photoId}`,
      { display_name: displayName },
    );
    return response.data.photo;
  },

  /**
   * Convert S3 URL to proxy URL
   * S3 URLs return 503 errors when accessed directly from the browser,
   * so we route them through our backend proxy.
   */
  s3UrlToProxyUrl(url: string): string {
    // Check if it's an S3 URL
    if (!url.includes('.s3.') || !url.includes('.amazonaws.com')) {
      return url; // Not an S3 URL, return as-is
    }
    try {
      const urlObj = new URL(url);
      // Extract the key from pathname (e.g., /photos/session-id/file.jpg)
      const key = urlObj.pathname.slice(1); // Remove leading slash
      return `${API_BASE_URL}/photos/proxy/${key}`;
    } catch {
      return url; // URL parsing failed, return as-is
    }
  },

  /**
   * Get thumbnail URL for a photo
   * Handles both upload response (has thumbnail_url) and session details (has file_path)
   * Routes S3 URLs through our proxy to avoid CORS/access issues
   */
  getThumbnailUrl(photo: Photo): string {
    // If backend already computed the thumbnail URL, use it directly
    if (photo.thumbnail_url) {
      // Check if it's a proxy URL (already processed)
      if (photo.thumbnail_url.startsWith('/api/photos/proxy/')) {
        return `${API_BASE_URL.replace('/api', '')}${photo.thumbnail_url}`;
      }
      // S3 URLs need to go through proxy
      if (photo.thumbnail_url.startsWith('http')) {
        return this.s3UrlToProxyUrl(photo.thumbnail_url);
      }
      // Local URL might need base URL prepended
      return photo.thumbnail_url.startsWith('/')
        ? `${API_BASE_URL.replace('/api', '')}${photo.thumbnail_url}`
        : photo.thumbnail_url;
    }
    // Fallback: derive from file_path
    // file_path format: "photos/sessionId/photoId.ext" (no leading slash) or S3 URL
    if (photo.file_path) {
      // If file_path is S3 URL, derive thumbnail URL and route through proxy
      if (photo.file_path.startsWith('http')) {
        const thumbnailS3Url = photo.file_path
          .replace('/photos/', '/thumbnails/')
          .replace(/\.(jpg|jpeg|png|webp)$/i, '_thumb.jpg');
        return this.s3UrlToProxyUrl(thumbnailS3Url);
      }
      // Local path fallback
      const thumbnailPath = photo.file_path
        .replace(/^(\/)?photos\//, 'thumbnails/')
        .replace(/\.(jpg|jpeg|png|webp)$/i, '_thumb.jpg');
      return `${API_BASE_URL.replace('/api', '')}/uploads/${thumbnailPath}`;
    }
    // Emergency fallback - shouldn't happen
    console.warn('Photo missing both thumbnail_url and file_path:', photo);
    return '';
  },

  /**
   * Get full-size photo URL
   * Handles both upload response (has url) and session details (has file_path)
   * Routes S3 URLs through our proxy to avoid CORS/access issues
   */
  getPhotoUrl(photo: Photo): string {
    // If backend already computed the URL, use it directly
    if (photo.url) {
      // Check if it's a proxy URL (already processed)
      if (photo.url.startsWith('/api/photos/proxy/')) {
        return `${API_BASE_URL.replace('/api', '')}${photo.url}`;
      }
      // S3 URLs need to go through proxy
      if (photo.url.startsWith('http')) {
        return this.s3UrlToProxyUrl(photo.url);
      }
      // Local URL might need base URL prepended
      return photo.url.startsWith('/')
        ? `${API_BASE_URL.replace('/api', '')}${photo.url}`
        : photo.url;
    }
    // Fallback: derive from file_path
    if (photo.file_path) {
      // S3 URLs need to go through proxy
      if (photo.file_path.startsWith('http')) {
        return this.s3UrlToProxyUrl(photo.file_path);
      }
      // Local path
      return `${API_BASE_URL.replace('/api', '')}/uploads/${photo.file_path}`;
    }
    // Emergency fallback - shouldn't happen
    console.warn('Photo missing both url and file_path:', photo);
    return '';
  },
};

// ============================================================================
// Feedback API
// ============================================================================

export interface FeedbackSummary {
  total_photos: number;
  total_feedback: number;
  approved_count: number;
  needs_changes_count: number;
  rejected_count: number;
  pending_count: number;
}

export interface SubmitFeedbackInput {
  approval_status: 'approve' | 'needs_changes' | 'rejected';
  feedback_text?: string;
}

export interface FeedbackResponse {
  feedback: Feedback;
  summary: FeedbackSummary;
  message: string;
}

export const feedbackAPI = {
  /**
   * Submit feedback for a photo (customer access)
   */
  async submit(
    sessionId: string,
    photoId: string,
    input: SubmitFeedbackInput,
  ): Promise<FeedbackResponse> {
    const response = await client.post<FeedbackResponse>(
      `/feedback/${sessionId}/${photoId}`,
      input,
    );
    return response.data;
  },

  /**
   * Get all feedback for a session
   */
  async getBySession(sessionId: string): Promise<{ feedback: Feedback[]; summary: FeedbackSummary }> {
    const response = await client.get<{ feedback: Feedback[]; summary: FeedbackSummary }>(
      `/feedback/${sessionId}`,
    );
    return response.data;
  },

  /**
   * Get feedback summary for a session
   */
  async getSummary(sessionId: string): Promise<{ summary: FeedbackSummary }> {
    const response = await client.get<{ summary: FeedbackSummary }>(
      `/feedback/${sessionId}/summary`,
    );
    return response.data;
  },
};

// ============================================================================
// Certificates API
// ============================================================================

export interface Certificate {
  id: string;
  session_id: string;
  pdf_filename: string;
  pdf_file_size_bytes: number;
  generated_at: string;
  download_url: string;
}

export interface CertificateResponse {
  success: boolean;
  certificate: Certificate;
}

export interface CertificateStatusResponse {
  success: boolean;
  certificate: Certificate | null;
  can_generate?: boolean;
}

export const certificatesAPI = {
  /**
   * Generate certificate for a session (or return cached if exists)
   */
  async generate(sessionId: string): Promise<CertificateResponse> {
    const response = await client.post<CertificateResponse>(
      `/certificates/${sessionId}/generate`,
    );
    return response.data;
  },

  /**
   * Get certificate status for a session
   */
  async getStatus(sessionId: string): Promise<CertificateStatusResponse> {
    const response = await client.get<CertificateStatusResponse>(
      `/certificates/${sessionId}`,
    );
    return response.data;
  },

  /**
   * Delete certificate to allow regeneration (admin only)
   */
  async delete(sessionId: string): Promise<{ success: boolean; message: string }> {
    const response = await client.delete<{ success: boolean; message: string }>(
      `/certificates/${sessionId}`,
    );
    return response.data;
  },

  /**
   * Get download URL for a certificate
   */
  getDownloadUrl(sessionId: string): string {
    return `${API_BASE_URL}/certificates/${sessionId}/download`;
  },
};

// ============================================================================
// Settings API
// ============================================================================

export interface AppSettings {
  default_expiration_days: number;
  default_notify_admin: boolean;
  default_max_revisions: number;
  reminder_enabled: boolean;
  reminder_days_before: number;
  reminder_time: string;
  admin_notification_email: string;
  business_name: string;
  support_email: string;
}

export interface SettingsResponse {
  success: boolean;
  settings: AppSettings;
  message?: string;
}

export const settingsAPI = {
  /**
   * Get all admin settings
   */
  async get(): Promise<SettingsResponse> {
    const response = await client.get<SettingsResponse>('/settings');
    return response.data;
  },

  /**
   * Update admin settings
   */
  async update(settings: Partial<AppSettings>): Promise<SettingsResponse> {
    const response = await client.put<SettingsResponse>('/settings', settings);
    return response.data;
  },
};

// ============================================================================
// Storage API
// ============================================================================

export interface S3Stats {
  totalSizeBytes: number;
  totalSizeMB: string;
  totalSizeGB: string;
  photoCount: number;
  thumbnailCount: number;
  storageMode: 's3' | 'local';
  bucketName: string;
  region: string;
  s3Available: boolean;
  s3Status: 'connected' | 'error' | 'not_configured';
  s3Error?: string;
}

export interface LocalStorageStats {
  totalSizeBytes: number;
  totalSizeMB: string;
  photoCount: number;
  thumbnailCount: number;
  uploadsDir: string;
  exists: boolean;
}

export interface StorageStatsResponse {
  success: boolean;
  s3: S3Stats;
  local: LocalStorageStats;
  currentMode: 's3' | 'local';
  s3Available: boolean;
}

export interface OrphanedFile {
  key: string;
  sizeBytes: number;
  sessionId: string;
  type: 'photo' | 'thumbnail';
  lastModified: string;
}

export interface GhostReference {
  photoId: string;
  sessionId: string;
  path: string;
  type: 'photo' | 'thumbnail';
}

export interface AuditResult {
  orphanedFiles: OrphanedFile[];
  ghostReferences: GhostReference[];
  orphanedSizeBytes: number;
  orphanedSizeMB: string;
  totalS3Objects: number;
  totalDbRecords: number;
  auditedAt: string;
}

export interface StorageAuditResponse {
  success: boolean;
  audit: AuditResult;
}

export interface DeleteOrphansResponse {
  success: boolean;
  deleted: number;
  failed: number;
}

export interface BucketResetResponse {
  success: boolean;
  deleted: number;
  failed: number;
  message: string;
}

export interface StorageFile {
  key: string;
  sessionId: string;
  type: 'photo' | 'thumbnail';
  sizeBytes: number;
  thumbnailUrl: string;
  lastModified: string;
}

export interface StorageFilesResponse {
  success: boolean;
  files: StorageFile[];
  totalCount: number;
  totalSizeBytes: number;
}

export interface LocalResetResponse {
  success: boolean;
  deleted: number;
  failed: number;
  message: string;
}

export const storageAPI = {
  /**
   * Get storage statistics (S3 and local)
   */
  async getStats(): Promise<StorageStatsResponse> {
    const response = await client.get<StorageStatsResponse>('/settings/storage-stats');
    return response.data;
  },

  /**
   * Run storage audit to find orphans and ghosts
   */
  async runAudit(): Promise<StorageAuditResponse> {
    const response = await client.get<StorageAuditResponse>('/settings/storage-audit');
    return response.data;
  },

  /**
   * Delete orphaned files from S3
   */
  async deleteOrphans(keys: string[]): Promise<DeleteOrphansResponse> {
    const response = await client.delete<DeleteOrphansResponse>('/settings/storage-orphans', {
      data: { keys },
    });
    return response.data;
  },

  /**
   * Reset (empty) the entire S3 bucket
   * Requires confirmation code matching bucket name
   */
  async resetBucket(confirmationCode: string): Promise<BucketResetResponse> {
    const response = await client.delete<BucketResetResponse>('/settings/storage-reset', {
      data: { confirmationCode },
    });
    return response.data;
  },

  /**
   * List all files in storage (S3 or local)
   */
  async getFiles(source: 's3' | 'local'): Promise<StorageFilesResponse> {
    const response = await client.get<StorageFilesResponse>('/settings/storage-files', {
      params: { source },
    });
    return response.data;
  },

  /**
   * Reset (empty) local storage
   * Requires confirmation code "DELETE LOCAL"
   */
  async resetLocalStorage(confirmationCode: string): Promise<LocalResetResponse> {
    const response = await client.delete<LocalResetResponse>('/settings/local-storage-reset', {
      data: { confirmationCode },
    });
    return response.data;
  },
};

// ============================================================================
// Users API
// ============================================================================

export interface CreateUserInput {
  username: string;
  password: string;
  role?: Role;
  email?: string;
  display_name?: string;
}

export interface UpdateUserInput {
  username?: string;
  role?: Role;
  email?: string | null;
  display_name?: string | null;
  is_active?: boolean;
}

export interface UserCounts {
  total: number;
  byRole: Record<Role, number>;
}

export const usersAPI = {
  /**
   * Get all users
   */
  async getAll(): Promise<{ users: User[] }> {
    const response = await client.get<{ users: User[] }>('/users');
    return response.data;
  },

  /**
   * Get user counts by role
   */
  async getCounts(): Promise<UserCounts> {
    const response = await client.get<UserCounts>('/users/counts');
    return response.data;
  },

  /**
   * Get a single user by ID
   */
  async getById(id: number): Promise<{ user: User }> {
    const response = await client.get<{ user: User }>(`/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user
   */
  async create(input: CreateUserInput): Promise<{ user: User }> {
    const response = await client.post<{ user: User }>('/users', input);
    return response.data;
  },

  /**
   * Update a user
   */
  async update(id: number, input: UpdateUserInput): Promise<{ user: User }> {
    const response = await client.put<{ user: User }>(`/users/${id}`, input);
    return response.data;
  },

  /**
   * Delete (deactivate) a user
   */
  async delete(id: number): Promise<{ message: string }> {
    const response = await client.delete<{ message: string }>(`/users/${id}`);
    return response.data;
  },

  /**
   * Change a user's password
   */
  async changePassword(id: number, password: string): Promise<{ message: string }> {
    const response = await client.put<{ message: string }>(`/users/${id}/password`, { password });
    return response.data;
  },

  /**
   * Reactivate a deactivated user
   */
  async reactivate(id: number): Promise<{ user: User }> {
    const response = await client.post<{ user: User }>(`/users/${id}/reactivate`);
    return response.data;
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get stored token
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Check if user is authenticated (has token)
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem(TOKEN_KEY);
};

/**
 * Clear authentication
 */
export const clearAuth = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};
